import { useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useFocusTrap } from '../lib/hooks';
import { useT } from '../i18n';
import { Button } from './Button';

interface BaseProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Blocking dialogs (e.g. mandatory re-auth) still close with Esc but have no backdrop dismissal. */
  dismissOnBackdrop?: boolean;
}

function CloseButton({ onClose }: { onClose: () => void }) {
  const t = useT();
  return (
    <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
      <X size={20} strokeWidth={1.5} aria-hidden="true" />
    </button>
  );
}

/** Modal dialog: traps focus, Esc closes, focus returns to the trigger (§8.4, §11.3). */
export function Dialog({ open, onClose, title, description, children, footer, wide, dismissOnBackdrop = true }: BaseProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  useFocusTrap(ref, open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <div className="overlay" onClick={dismissOnBackdrop ? onClose : undefined} aria-hidden="true" />
      <div
        ref={ref}
        className={['dialog', wide && 'dialog--wide'].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
      >
        <div className="dialog__header">
          <h2 className="dialog__title" id={titleId}>
            {title}
          </h2>
          <CloseButton onClose={onClose} />
        </div>
        <div className="dialog__body">
          {description && (
            <p id={descId} className="muted">
              {description}
            </p>
          )}
          {children}
        </div>
        {footer && <div className="dialog__footer">{footer}</div>}
      </div>
    </>,
    document.body,
  );
}

/** Right-hand drawer for drill-down and details: 480px desktop, full screen on mobile (§8.4). */
export function Drawer({ open, onClose, title, description, children, footer, wide }: BaseProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <div className="overlay" onClick={onClose} aria-hidden="true" />
      <aside ref={ref} className={['drawer', wide && 'drawer--wide'].filter(Boolean).join(' ')} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="drawer__header">
          <div className="stack-sm" style={{ gap: 4, minWidth: 0 }}>
            <h2 className="dialog__title" id={titleId}>
              {title}
            </h2>
            {description && <div className="muted small">{description}</div>}
          </div>
          <CloseButton onClose={onClose} />
        </div>
        <div className="drawer__body">{children}</div>
        {footer && <div className="drawer__footer">{footer}</div>}
      </aside>
    </>,
    document.body,
  );
}

/** Bottom sheet on mobile, centred dialog on desktop (e.g. "Ik weet het niet zeker"). */
export function Sheet({ open, onClose, title, description, children, footer }: BaseProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <div className="overlay" onClick={onClose} aria-hidden="true" />
      <div ref={ref} className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="sheet__grip" aria-hidden="true" />
        <div className="dialog__header">
          <div className="stack-sm" style={{ gap: 4 }}>
            <h2 className="dialog__title" id={titleId}>
              {title}
            </h2>
            {description && <p className="muted">{description}</p>}
          </div>
          <CloseButton onClose={onClose} />
        </div>
        <div className="dialog__body">{children}</div>
        {footer && <div className="dialog__footer">{footer}</div>}
      </div>
    </>,
    document.body,
  );
}

/**
 * Confirmation for irreversible actions only (§9.3). The confirm button names the action ("Regel intrekken"),
 * never "OK"; the body states the impact.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel,
  danger = true,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<unknown>;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
}) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} data-autofocus>
            {t('common.cancel')}
          </Button>
          <Button variant={danger ? 'danger-solid' : 'primary'} onClick={run} loading={busy || loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div>{children}</div>
    </Dialog>
  );
}
